import { type ApolloClient, gql, type NormalizedCacheObject } from '@apollo/client/core';
import type { VpError } from '../../../generated/internal.graphql';
import * as VPError from '../../../VPError';
import { type InternalApiError, toApiErrors } from '../errors';
import type { VPResult } from '../../VPResult';
import { CONNECTION_METADATA_FIELDS, type ConnectionArguments } from '../../connection/Api';
import * as TicketLayout from './TicketLayout';
import { INTERNAL_TICKET_LAYOUT_FIELDS } from './fragments';
import { ACCOUNT_AGNOSTIC } from '../../accountContext';

/**
 * Ticket-layout administration for VenuePilot staff — the printable layouts the box office and
 * the desktop printer app render tickets from.
 *
 * This surface took the long way round. It started on the **legacy** schema (`apiClient`,
 * `generated/graphql`, nested under `app { … }`), moved to AppSchema in an earlier migration —
 * whose `@deprecated "Moved to ticketLayout on AppSchema (x-vp-api: app)"` markers are still
 * visible in `default.schema.graphql` — and ADR-009 then moved it on again to InternalSchema,
 * because `ticket_layouts` / `ticket_layout` are guarded by `require_superadmin!` and
 * `TicketLayoutType < Types::SuperadminObject`: superadmin-only, and so not something to leave
 * in a schema now published to every account holder. Auth is the superadmin Rails session
 * cookie — a bearer token never satisfies the gate, and it only works in the Rails-embedded
 * admin against a same-origin `/graphql`, never the standalone dev server — so everything here
 * goes through `internalGqlApiClient`.
 *
 * A non-superadmin caller is rejected with HTTP 401 before the query is parsed, which Apollo
 * *throws* rather than returning a payload. Every function below therefore catches and
 * classifies via `toApiErrors`, so callers can branch on `isUnauthorized` and say "VenuePilot
 * staff only" instead of rendering an empty table — and so an expected authorization outcome
 * does not reach Sentry as an exception. **Nothing here throws.**
 *
 * Two result shapes, matching `Internal/AppConfig/Api.ts`: reads resolve `VPResult`, mutations
 * resolve `MutationResult`. `saveTicketLayout`'s payload carries its entity, so it needs no
 * follow-up refetch.
 */

export type MutationResult = { success: boolean; errors: InternalApiError[] };

const INTERNAL_TICKET_LAYOUTS_QUERY = gql`
  query InternalTicketLayouts($after: String, $before: String, $first: Int, $last: Int) {
    ticketLayouts(after: $after, before: $before, first: $first, last: $last) {
      edges {
        cursor
        node {
          ...InternalTicketLayoutFields
        }
      }
      pageInfo {
        ...ConnectionMetadataFields
      }
    }
  }
  ${INTERNAL_TICKET_LAYOUT_FIELDS}
  ${CONNECTION_METADATA_FIELDS}
`;

const INTERNAL_TICKET_LAYOUT_QUERY = gql`
  query InternalTicketLayout($id: Int!) {
    ticketLayout(id: $id) {
      ...InternalTicketLayoutFields
    }
  }
  ${INTERNAL_TICKET_LAYOUT_FIELDS}
`;

const INTERNAL_SAVE_TICKET_LAYOUT_MUTATION = gql`
  mutation InternalSaveTicketLayout($ticketLayout: TicketLayoutAttributes!) {
    saveTicketLayout(input: { ticketLayout: $ticketLayout }) {
      ticketLayout {
        ...InternalTicketLayoutFields
      }
      errors {
        message
        path
      }
    }
  }
  ${INTERNAL_TICKET_LAYOUT_FIELDS}
`;

const toErrors = (errors?: VpError[] | null): InternalApiError[] =>
  VPError.fromGqlCollection(errors ?? []);

/**
 * A failed mutation with an empty `errors` list leaves the caller nothing to render. Today's
 * resolvers always populate one — but a null payload does not, so fall back rather than report
 * a silent failure.
 */
const failed = (errors: InternalApiError[], fallback: string): MutationResult => ({
  success: false,
  errors: errors.length ? errors : [{ path: '', message: fallback }],
});

export const fetchTicketLayouts = async (
  client: ApolloClient<NormalizedCacheObject>,
  params: ConnectionArguments = {},
): Promise<VPResult<TicketLayout.TicketLayoutConnection, InternalApiError[]>> => {
  try {
    const { data } = await client.query({
      query: INTERNAL_TICKET_LAYOUTS_QUERY,
      // The index re-reads after every save; a cached page would hand the operator their own
      // stale row straight back.
      fetchPolicy: 'network-only',
      // Account-agnostic: a superadmin-only registry of templates, which is why ADR-009 took it off
      // AppSchema. No account owns a layout.
      context: ACCOUNT_AGNOSTIC,
      variables: {
        after: params.after,
        before: params.before,
        first: params.first,
        last: params.last,
      },
    });

    // `ticketLayouts` is nullable. An absent connection is a backend fault, not an empty page —
    // resolving it as one would render "no ticket layouts" and hide the failure.
    if (!data?.ticketLayouts) {
      return { success: false, errors: [{ path: '', message: 'Failed to fetch ticket layouts.' }] };
    }

    return { success: true, data: TicketLayout.fromGqlConnection(data.ticketLayouts) };
  } catch (error) {
    return { success: false, errors: toApiErrors(error) };
  }
};

export const fetchTicketLayout = async (
  client: ApolloClient<NormalizedCacheObject>,
  id: number,
): Promise<VPResult<TicketLayout.TicketLayout | undefined, InternalApiError[]>> => {
  try {
    const { data } = await client.query({
      query: INTERNAL_TICKET_LAYOUT_QUERY,
      fetchPolicy: 'network-only',
      variables: { id },
      // Account-agnostic: same registry, addressed by layout id.
      context: ACCOUNT_AGNOSTIC,
    });

    // A missing layout is an answer (bad id), not a failure — don't dress it up as one.
    return {
      success: true,
      data: data?.ticketLayout ? TicketLayout.fromGql(data.ticketLayout) : undefined,
    };
  } catch (error) {
    return { success: false, errors: toApiErrors(error) };
  }
};

export const saveTicketLayout = async (
  client: ApolloClient<NormalizedCacheObject>,
  ticketLayout: TicketLayout.TicketLayoutInput,
): Promise<MutationResult & { ticketLayout?: TicketLayout.TicketLayout }> => {
  try {
    const { data } = await client.mutate({
      mutation: INTERNAL_SAVE_TICKET_LAYOUT_MUTATION,
      variables: { ticketLayout },
      fetchPolicy: 'network-only',
      // Account-agnostic: same registry, and a created layout belongs to no account either.
      context: ACCOUNT_AGNOSTIC,
    });

    const errors = toErrors(data?.saveTicketLayout?.errors);
    const saved = data?.saveTicketLayout?.ticketLayout;

    if (errors.length || !saved) {
      return failed(errors, 'Failed to save the ticket layout.');
    }

    return { success: true, errors: [], ticketLayout: TicketLayout.fromGql(saved) };
  } catch (error) {
    return { success: false, errors: toApiErrors(error) };
  }
};
